import { useRef, useMemo } from 'react';
import { useThree, useFrame } from '@react-three/fiber';
import * as THREE from 'three';

export default function Background({
  backgroundSpeed = 1.0,
  backgroundIntensity = 40.0,
  backgroundFrequency = 7.0,
  backgroundAmplitude = 0.15
}) {
  const materialRef = useRef();
  const { size } = useThree();

  const shaderMaterial = useMemo(() => new THREE.ShaderMaterial({
    uniforms: {
      uTime: { value: 0 },
      uResolution: { value: new THREE.Vector2(size.width, size.height) },
      uSpeed: { value: backgroundSpeed },
      uIntensity: { value: backgroundIntensity },
      uFrequency: { value: backgroundFrequency },
      uAmplitude: { value: backgroundAmplitude }
    },
    vertexShader: `
      varying vec2 vUv;
      void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      uniform float uTime;
      uniform vec2 uResolution;
      uniform float uSpeed;
      uniform float uIntensity;
      uniform float uFrequency;
      uniform float uAmplitude;
      varying vec2 vUv;

      void main() {
          vec3 color = vec3(0.0);
          float z = uTime * uSpeed;

          for (int i = 0; i < 3; i++) {
              vec2 uv = vUv;
              vec2 p = vUv.xy;
              p -= uv.y + 0.1;
              p.y *= uResolution.y / uResolution.x;
              float l = length(p);

              float angleOffset = float(i) * 0.2;
              float wave = cos(z * uFrequency + l * 10.0 + angleOffset) * uAmplitude;
              uv += p / l * wave * cos(l * 110.0 - z + angleOffset);

              color[i] += 0.01 / length(mod(uv, 1.0) - 0.5);
          }

          color /= length(vUv - 0.5) * uIntensity;

          gl_FragColor = vec4(color, 1.0);
      }
    `,
    side: THREE.BackSide
  }), []);

  useFrame(({ clock }) => {
    if (materialRef.current) {
      materialRef.current.uniforms.uTime.value = clock.getElapsedTime();
      materialRef.current.uniforms.uResolution.value.set(size.width, size.height);
      materialRef.current.uniforms.uSpeed.value = backgroundSpeed;
      materialRef.current.uniforms.uIntensity.value = backgroundIntensity;
      materialRef.current.uniforms.uFrequency.value = backgroundFrequency;
      materialRef.current.uniforms.uAmplitude.value = backgroundAmplitude;
    }
  });

  return (
    <mesh scale={[130, 200, 100]} position={[0, 0, -10]} rotation={[0, 0, 0]}>
      <icosahedronGeometry args={[1, 64]} />
      <primitive object={shaderMaterial} attach="material" ref={materialRef} />
    </mesh>
  );
}
